/**
 * SettlementSettingsCard — per-tenant card-batch (settlement) time.
 *
 * Each merchant's Authorize.net account runs a daily settlement batch at a
 * time THEY configure (e.g. 8:00 PM). The platform can't observe when
 * that batch runs, so it uses this time to decide, by the clock, whether a
 * charge is still "unsettled" (Void) or "settled" (Refund) on the
 * Transactions tab. Owners set it here to match their gateway account.
 *
 * UX note: this used to be a native <input type="time">, which produces
 * inconsistent browser-native "Invalid value" tooltips. Replaced with
 * hour/minute/AM-PM dropdowns — they can never hold an invalid value, are
 * fully styled, and read clearly. The gateway remains the arbiter; this
 * only chooses which action the desk offers first.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminPaymentsApi } from '../../lib/api.js';

type Props = { readonly apiUrl: string };

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; timezone: string };

const DEFAULT_24 = '20:00'; // platform default batch time (8:00 PM)
const MINUTE_CHOICES = ['00', '15', '30', '45'];

function parse24(v: string): { h12: number; min: string; ampm: 'AM' | 'PM' } {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(v);
  const total = Number(m?.[1] ?? '20');
  const min = m?.[2] ?? '00';
  const ampm: 'AM' | 'PM' = total < 12 ? 'AM' : 'PM';
  const h12 = total % 12 === 0 ? 12 : total % 12;
  return { h12, min, ampm };
}

function to24(h12: number, min: string, ampm: 'AM' | 'PM'): string {
  let h = h12 % 12;
  if (ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${min}`;
}

function to12hLabel(v: string): string {
  const { h12, min, ampm } = parse24(v);
  return `${h12}:${min} ${ampm}`;
}

export function SettlementSettingsCard({ apiUrl }: Props): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [value, setValue] = useState<string>(DEFAULT_24); // canonical "HH:MM" 24h
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await adminPaymentsApi.getSettlementConfig(apiUrl);
    if (res.error || !res.data) {
      setState({
        kind: 'error',
        message: res.error ?? 'Could not load the batch-time setting',
      });
      return;
    }
    // Null in the DB ⇒ show the default so the picker always holds a valid time.
    setValue(res.data.settlementCutoffLocalTime || DEFAULT_24);
    setState({ kind: 'ready', timezone: res.data.timezone });
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const parsed = useMemo(() => parse24(value), [value]);
  const minuteChoices = useMemo(
    () =>
      MINUTE_CHOICES.includes(parsed.min)
        ? MINUTE_CHOICES
        : [...MINUTE_CHOICES, parsed.min].sort(),
    [parsed.min],
  );

  const update = useCallback(
    (next: { h12?: number; min?: string; ampm?: 'AM' | 'PM' }) => {
      setMessage(null);
      setError(null);
      setValue(
        to24(
          next.h12 ?? parsed.h12,
          next.min ?? parsed.min,
          next.ampm ?? parsed.ampm,
        ),
      );
    },
    [parsed],
  );

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    const res = await adminPaymentsApi.setSettlementConfig(apiUrl, value);
    setSaving(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setMessage(`Saved. Batch time set to ${to12hLabel(value)}.`);
  }, [apiUrl, value]);

  return (
    <article
      className="ferro-card"
      aria-label="Card batch time"
      data-testid="settlement-settings"
    >
      <header className="ferro-card__header">
        <h2 className="ferro-section__subtitle">Card batch (settlement) time</h2>
      </header>
      <p className="ferro-muted">
        The daily time your Authorize.net account settles its batch. The
        Transactions tab uses it to decide whether a charge can be voided
        (before the batch) or must be refunded (after). Match it to the
        batch time configured on your gateway account.
      </p>

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <form
          className="ferro-field"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <span className="ferro-field__label">
            Batch time ({state.timezone})
          </span>
          <div className="ferro-inline-confirm">
            <select
              className="ferro-field__input"
              aria-label="Hour"
              value={parsed.h12}
              onChange={(e) => update({ h12: Number(e.target.value) })}
              data-testid="settlement-hour"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <span aria-hidden="true">:</span>
            <select
              className="ferro-field__input"
              aria-label="Minute"
              value={parsed.min}
              onChange={(e) => update({ min: e.target.value })}
              data-testid="settlement-minute"
            >
              {minuteChoices.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="ferro-field__input"
              aria-label="AM or PM"
              value={parsed.ampm}
              onChange={(e) =>
                update({ ampm: e.target.value as 'AM' | 'PM' })
              }
              data-testid="settlement-ampm"
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
          <p className="ferro-muted">Settles daily at {to12hLabel(value)}.</p>
          <div className="ferro-table__actions">
            <button
              type="submit"
              className="ferro-btn ferro-btn--primary"
              disabled={saving}
              data-testid="settlement-save"
            >
              {saving ? 'Saving…' : 'Save batch time'}
            </button>
            {value !== DEFAULT_24 ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => update({ ...parse24(DEFAULT_24) })}
              >
                Reset to 8:00 PM
              </button>
            ) : null}
          </div>
          {message ? (
            <p className="ferro-muted" data-testid="settlement-message">
              {message}
            </p>
          ) : null}
          {error ? (
            <div className="ferro-dashboard__error" role="alert">
              <p>{error}</p>
            </div>
          ) : null}
        </form>
      ) : null}
    </article>
  );
}
